import { useEffect } from "react";

/**
 * Blocks the context menu across the portal and reloads the page whenever
 * devtools is opened, so the Network tab never accumulates API traffic.
 *
 * Never armed on localhost - local development keeps devtools fully usable,
 * including when serving a production build from the machine itself.
 */

const LOCAL_HOSTS = ["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"];

const isLocalhost = () => {
  if (typeof window === "undefined") return true;
  const host = window.location.hostname;
  return LOCAL_HOSTS.includes(host) || host.endsWith(".localhost");
};

const ENABLED = process.env.NODE_ENV === "production" && !isLocalhost();

// A `debugger` statement pauses far longer than this when devtools is open.
const DEBUGGER_THRESHOLD = 100;
const CHECK_INTERVAL = 1000;
// Let the app paint before the first probe, so a misfire can never stop it
// from rendering at all.
const START_DELAY = 2000;

// Circuit breaker: if detection somehow misfires on a device we did not
// anticipate, the portal must still open rather than reload forever.
const TRIP_KEY = "__sg_reloads";
const TRIP_LIMIT = 3;
const TRIP_WINDOW = 15000;

const readTrip = () => {
  try {
    return JSON.parse(sessionStorage.getItem(TRIP_KEY)) || { n: 0, at: 0 };
  } catch (err) {
    return { n: 0, at: 0 };
  }
};

const writeTrip = (value) => {
  try {
    sessionStorage.setItem(TRIP_KEY, JSON.stringify(value));
  } catch (err) {
    /* storage unavailable - guard simply loses its memory */
  }
};

// True once we have reloaded too many times in quick succession.
const isTripped = () => {
  const { n, at } = readTrip();
  return n >= TRIP_LIMIT && Date.now() - at < TRIP_WINDOW;
};

const recordReload = () => {
  const { n, at } = readTrip();
  const recent = Date.now() - at < TRIP_WINDOW;
  writeTrip({ n: recent ? n + 1 : 1, at: Date.now() });
};

const isBlockedKey = (e) => {
  const key = (e.key || "").toLowerCase();

  // F12
  if (key === "f12") return true;

  // Ctrl/Cmd + Shift + I / J / C  (devtools, console, element picker)
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && ["i", "j", "c"].includes(key)) {
    return true;
  }

  // Cmd + Alt + I / J / C  (Safari / Chrome on macOS)
  if (e.metaKey && e.altKey && ["i", "j", "c"].includes(key)) {
    return true;
  }

  // Ctrl/Cmd + U  (view source)
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && key === "u") return true;

  return false;
};

/**
 * Text fields keep their native context menu so long-press paste on mobile and
 * right-click paste on desktop continue to work. Nothing sensitive is exposed:
 * the menu on an editable field only offers cut/copy/paste/select.
 */
const isEditable = (el) => {
  if (!el || typeof el.closest !== "function") return false;
  return !!el.closest(
    'input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable=""], [contenteditable="true"]'
  );
};

const useSecurityGuard = () => {
  useEffect(() => {
    if (!ENABLED) {
      return undefined;
    }

    let reloading = false;
    let timer = null;
    let starter = null;

    const reload = () => {
      if (reloading) return;
      reloading = true;
      recordReload();
      window.location.reload();
    };

    const onContextMenu = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
    };

    const onKeyDown = (e) => {
      if (isBlockedKey(e)) {
        e.preventDefault();
        e.stopPropagation();
        reload();
      }
    };

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("keydown", onKeyDown, true);

    // Pausing on `debugger` is the one signal that is genuinely specific to
    // devtools being open. Viewport-size heuristics are not usable here: mobile
    // browser chrome and desktop page zoom both skew inner/outer dimensions.
    const detectByDebugger = () => {
      const start = performance.now();
      // eslint-disable-next-line no-debugger
      debugger;
      return performance.now() - start > DEBUGGER_THRESHOLD;
    };

    if (!isTripped()) {
      starter = setTimeout(() => {
        timer = setInterval(() => {
          if (detectByDebugger()) reload();
        }, CHECK_INTERVAL);
      }, START_DELAY);
    }

    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("keydown", onKeyDown, true);
      if (starter) clearTimeout(starter);
      if (timer) clearInterval(timer);
    };
  }, []);
};

export default useSecurityGuard;
